export type StateCode = keyof typeof STATE_DATA;
export type StateName = (typeof STATE_DATA)[StateCode];

/**
 * Type guard that checks if a string is a valid US state code.
 *
 * @param stateCode - The string to check
 * @returns True if the string is a valid state code, false otherwise
 *
 * @example
 * isValidStateCode("AL") // => true
 * isValidStateCode("ZZ") // => false
 * isValidStateCode("Alabama") // => false
 */
export const isValidStateCode = (stateCode: string): stateCode is keyof typeof STATE_DATA => {
  return stateCode in STATE_DATA;
};

/**
 * Finds a state code from a state name or code input.
 * Handles both full names ("Alabama") and codes ("AL").
 * Case-insensitive matching.
 *
 * @param stateName - The state name or code to look up
 * @returns The matching state code if found, undefined otherwise
 *
 * @example
 * findStateCode("Alabama") // => "AL"
 * findStateCode("AL") // => "AL"
 * findStateCode("Invalid") // => undefined
 * findStateCode(null) // => undefined
 */
export const findStateCode = (stateName: string | null | undefined): StateCode | undefined => {
  if (!stateName) return undefined;

  if (isValidStateCode(stateName)) return stateName;

  const normalizedStateName = stateName.toLowerCase();
  const stateEntry = Object.entries(STATE_DATA).find(([_, name]) => name.toLowerCase() === normalizedStateName);

  if (!stateEntry) return undefined;

  return isValidStateCode(stateEntry[0]) ? stateEntry[0] : undefined;
};

export const STATE_DATA = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
} as const;
